import { sessionEnded } from '../../../shared/api/sessionCoordinator';
import type { RealtimeBootstrap, RealtimeEnvelope } from '../types';

type Handlers = {
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
  onEvent?: (event: RealtimeEnvelope) => void;
};

export class RealtimeSignalingClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private manuallyClosed = false;
  private generation = 0;
  private refreshing = false;
  private reconnectDelayMs = 1500;

  constructor(
    private bootstrap: RealtimeBootstrap,
    private readonly handlers: Handlers,
    private readonly refreshBootstrap: () => Promise<RealtimeBootstrap>,
  ) {}

  connect() {
    if (this.refreshing) return;
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.manuallyClosed = false;
    void this.reconnect();
  }

  private openSocket() {
    const url = this.buildSignalingUrl();
    url.searchParams.set('token', this.bootstrap.callToken);
    url.searchParams.set('deviceId', this.bootstrap.deviceId);
    url.searchParams.set('userId', String(this.bootstrap.userId));
    url.searchParams.set('platform', this.bootstrap.platform);
    url.searchParams.set('appId', this.bootstrap.appId);

    const ws = new WebSocket(url.toString());
    let openedAt = 0;
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws || this.manuallyClosed) return;
      openedAt = Date.now();
      this.handlers.onOpen?.();
    };

    ws.onclose = () => {
      if (this.ws !== ws) return;
      // Upgrade alone does not prove authentication; short failures retain backoff.
      if (openedAt && Date.now() - openedAt >= 30000) this.reconnectDelayMs = 1500;
      this.ws = null;
      this.handlers.onClose?.();
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      if (this.ws !== ws || this.manuallyClosed) return;
      this.handlers.onError?.(new Error('Realtime signaling websocket unavailable'));
    };

    ws.onmessage = (messageEvent) => {
      if (this.ws !== ws || this.manuallyClosed) return;
      try {
        const parsed = JSON.parse(String(messageEvent.data)) as RealtimeEnvelope;
        if (parsed.protocolVersion !== 1 || parsed.type !== 'realtime' || !parsed.action.startsWith('rt.')) {
          return;
        }
        console.info('[realtime] received', parsed.action, {
          sessionId: parsed.sessionId,
          actorUserId: parsed.actorUserId,
          peerUserId: parsed.peerUserId,
        });
        this.handlers.onEvent?.(parsed);
      } catch (error) {
        this.handlers.onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    };
  }

  private scheduleReconnect() {
    if (this.manuallyClosed || this.reconnectTimer !== null) return;
    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(delay * 2, 30000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.reconnect();
    }, delay);
  }

  private async reconnect() {
    const generation = this.generation;
    this.refreshing = true;
    try {
      const bootstrap = await this.refreshBootstrap();
      if (this.manuallyClosed || generation !== this.generation) return;
      this.bootstrap = bootstrap;
      this.openSocket();
    } catch (error) {
      if (this.manuallyClosed || generation !== this.generation) return;
      if (sessionEnded(error)) this.manuallyClosed = true;
      this.handlers.onError?.(error instanceof Error ? error : new Error(String(error)));
      this.scheduleReconnect();
    } finally {
      this.refreshing = false;
    }
  }

  private buildSignalingUrl(): URL {
    // BFF bootstrap is the authoritative source for the public signaling
    // endpoint in split deployments. For example, the Web App page may be
    // served from https://app.locs.club while RTC signaling is exposed from
    // wss://api.locs.club/rtc-ws, so deriving from window.location alone would
    // point the socket at the wrong host.
    const url = new URL(this.bootstrap.signalingUrl || '/rtc-ws', window.location.origin);
    // Allow bootstrap to return either an HTTP(S) origin-style URL or an
    // already websocket-specific WS(S) URL. WebSocket requires ws/wss schemes.
    // If the current page is HTTPS, force wss even when an upstream proxy made
    // BFF derive ws://api.locs.club; browsers block insecure WebSockets from
    // secure pages as mixed content.
    if (window.location.protocol === 'https:' || url.protocol === 'https:' || url.protocol === 'wss:') {
      url.protocol = 'wss:';
    } else {
      // Local development and non-TLS test environments can still use ws.
      // If older bootstrap data omits signalingUrl, the relative /rtc-ws
      // fallback resolves against the current dev-server origin.
      url.protocol = 'ws:';
    }
    return url;
  }

  send<T extends Record<string, unknown>>(event: RealtimeEnvelope<T>) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Realtime signaling socket is not connected');
    }
    this.ws.send(JSON.stringify(event));
    if (event.action.startsWith('rt.')) {
      console.info('[realtime] sent', event.action, {
        sessionId: event.sessionId,
        peerUserId: event.peerUserId,
      });
    }
  }

  close() {
    this.manuallyClosed = true;
    this.generation += 1;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }
}
